import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useMutation, useApolloClient } from '@apollo/client';
import { useAuth } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import {
  portfolioIdAtom,
  latestSnapshotIdAtom,
  currentDashboardIdAtom,
  agentChatInjectionAtom,
  agentChatVisibleAtom,
} from '~/atoms/dashboardAtom';
import { EditPortfolioDialog } from '~/components/ui/common/EditPortfolioDialog';
import { CreatePortfolioStepperDialog } from '~/components/ui/common/CreatePortfolioStepperDialog';
import {
  DELETE_PORTFOLIO,
  GET_PORTFOLIO_BY_USER_ID,
  GET_ALL_PORTFOLIOS,
} from '~/graphql/queries/portfolio';
import { StreamChat } from '~/components/ui/misc/StreamChat/StreamChat';
import { useDashboards } from '~/hooks/useDashboard';
import { useRBAC } from '~/hooks/useRBAC';
import { usePortfolios } from '~/hooks/usePortfolios';
import '~/styles/component/bq-sidebar-v2.css';

// Custom hook to fetch app version
const useAppVersion = () => {
  const [version, setVersion] = useState<string>('');

  useEffect(() => {
    fetch('/version.json')
      .then((res) => res.json())
      .then((data) => setVersion(data.version))
      .catch(() => setVersion('unknown'));
  }, []);

  return version;
};

export const SidebarStaticV2 = ({
  isExpanded = false,
  onExpandToggle,
  sidebarWidth: externalSidebarWidth = 240,
  onSidebarWidthChange,
}: {
  isExpanded: boolean;
  onExpandToggle: (expanded: boolean) => void;
  sidebarWidth?: number;
  onSidebarWidthChange?: (width: number) => void;
}) => {
  const { portfolios: portfoliosData = [], loading: portfoliosLoading } =
    usePortfolios();
  const { data: dashboardsResponse } = useDashboards();
  const { hasAccess, canCreate, canDelete } = useRBAC();
  const appVersion = useAppVersion();
  const navigate = useNavigate();
  const location = useLocation();
  const isPopout = location.pathname.startsWith('/popout/');
  const toast = useRef<Toast>(null);
  const setPortfolioId = useSetAtom(portfolioIdAtom);
  const setLatestSnapshotId = useSetAtom(latestSnapshotIdAtom);
  const auth = useAuth();
  const apolloClient = useApolloClient();

  // Get portfolio ID from atom (set by dashboard component)
  const portfolioIdFromAtom = useAtomValue(portfolioIdAtom);
  const agentChatInjection = useAtomValue(agentChatInjectionAtom);

  // Parse dashboards response
  const _dashboards = Array.isArray(dashboardsResponse)
    ? dashboardsResponse
    : [];

  // Sort portfolios by name alphabetically
  const portfolios = [...portfoliosData].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  const [localSidebarWidth, setLocalSidebarWidth] =
    useState(externalSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [showChat, setShowChat] = useAtom(agentChatVisibleAtom);
  const [chatWidth, setChatWidth] = useState(400);

  // Get the current dashboard ID from the atom (set by DashboardPage)
  const dashboardId = useAtomValue(currentDashboardIdAtom);
  const [isChatResizing, setIsChatResizing] = useState(false);
  const isChatResizingRef = useRef(false);
  const [portfolioSearch, setPortfolioSearch] = useState('');
  const [editPortfolioVisible, setEditPortfolioVisible] = useState(false);
  const [createPortfolioVisible, setCreatePortfolioVisible] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const [deletePortfolio] = useMutation(DELETE_PORTFOLIO, {
    // Only refetch GET_ALL_PORTFOLIOS which doesn't require variables
    // Other queries will be refetched manually with proper variables
    refetchQueries: [GET_ALL_PORTFOLIOS],
  });

  // Sync external width changes
  useEffect(() => {
    setLocalSidebarWidth(externalSidebarWidth);
  }, [externalSidebarWidth]);

  const sidebarWidth = localSidebarWidth;

  // const [globalSearch] = useAtom<GlobalSearchPopupRef | null>(
  //   globalSearchRefAtom,
  // );

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (newWidth >= 56 && newWidth <= 400) {
        setLocalSidebarWidth(newWidth);
        onSidebarWidthChange?.(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, onSidebarWidthChange]);

  const isPortfolioActive = (portfolioId: string) => {
    return portfolioIdFromAtom === portfolioId;
  };

  const isViewActive = (viewName: string) => {
    const path = location.pathname;
    // Check new portfolio routes
    if (viewName === 'overview')
      return path.startsWith('/portfolio/') && path.endsWith('/overview');
    if (viewName === 'risk-opportunities')
      return path.startsWith('/portfolio/') && path.endsWith('/risks');
    if (viewName === 'analysis')
      return path.startsWith('/portfolio/') && path.endsWith('/analysis');
    if (viewName === 'ai-recommendations')
      return (
        path.startsWith('/portfolio/') && path.endsWith('/ai-recommendations')
      );
    if (viewName === 'upload')
      return path.startsWith('/portfolio/') && path.endsWith('/upload');
    return false;
  };

  // Filter portfolios based on search
  const filteredPortfolios = portfolios.filter((portfolio) =>
    portfolio.name.toLowerCase().includes(portfolioSearch.toLowerCase()),
  );

  const handleChatMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsChatResizing(true);
    isChatResizingRef.current = true;

    const startX = e.clientX;
    const startWidth = chatWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isChatResizingRef.current) return;

      const deltaX = e.clientX - startX;
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));

      setChatWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsChatResizing(false);
      isChatResizingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);
  };

  const handlePortfolioChange = (portfolioId: string) => {
    // Only update if portfolio actually changed
    if (portfolioIdFromAtom && portfolioIdFromAtom === portfolioId) {
      return; // Same portfolio, no need to do anything
    }

    // Update global atom so the rest of the app reacts to portfolio change
    setPortfolioId(portfolioId);
    // Clear the current snapshot ID when portfolio changes to prevent stale data
    setLatestSnapshotId(null);

    // If we're on a portfolio route, navigate to the same route with new portfolioId
    const currentPath = location.pathname;
    if (
      currentPath.includes('/portfolio/') &&
      currentPath !== '/portfolio/create'
    ) {
      if (currentPath.endsWith('/overview')) {
        navigate({
          to: '/portfolio/$portfolioId/overview',
          params: { portfolioId },
        });
      } else if (currentPath.endsWith('/risks')) {
        navigate({
          to: '/portfolio/$portfolioId/risks',
          params: { portfolioId },
        });
      } else if (currentPath.endsWith('/analysis')) {
        navigate({
          to: '/portfolio/$portfolioId/analysis',
          params: { portfolioId },
        });
      } else if (currentPath.endsWith('/ai-recommendations')) {
        navigate({
          to: '/portfolio/$portfolioId/ai-recommendations',
          params: { portfolioId },
        });
      } else if (currentPath.endsWith('/upload')) {
        navigate({
          to: '/portfolio/$portfolioId/upload',
          params: { portfolioId },
        });
      }
      // If on base portfolio route (without a view), navigate to overview
      else if (currentPath.match(/^\/portfolio\/[^/]+$/)) {
        navigate({
          to: '/portfolio/$portfolioId/overview',
          params: { portfolioId },
        });
      }
    }
    // If not on a portfolio route, navigate to the new portfolio's overview
    else if (!currentPath.includes('/portfolio-company/')) {
      navigate({
        to: '/portfolio/$portfolioId/overview',
        params: { portfolioId },
      });
    }
  };

  const handleDeletePortfolio = (portfolio: { id: string; name: string }) => {
    confirmDialog({
      message: `Are you sure you want to delete "${portfolio.name}"?`,
      header: 'Delete Portfolio',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await deletePortfolio({
            variables: { id: portfolio.id },
          });

          // Manually refetch queries with proper variables
          const refetchPromises: Promise<unknown>[] = [];

          // Refetch GET_ALL_PORTFOLIOS (no variables needed)
          refetchPromises.push(
            apolloClient.refetchQueries({
              include: [GET_ALL_PORTFOLIOS],
            }),
          );

          // Refetch GET_PORTFOLIO_BY_USER_ID if we have userId
          if (auth.userId) {
            refetchPromises.push(
              apolloClient.query({
                query: GET_PORTFOLIO_BY_USER_ID,
                variables: { userId: auth.userId },
                fetchPolicy: 'network-only',
              }),
            );
          }

          await Promise.all(refetchPromises);

          // If deleted portfolio was the active one, switch to another or clear
          if (portfolioIdFromAtom === portfolio.id) {
            const remainingPortfolios = portfolios.filter(
              (p) => p.id !== portfolio.id,
            );
            if (remainingPortfolios.length > 0) {
              handlePortfolioChange(remainingPortfolios[0].id);
            } else {
              setPortfolioId(null);
              setLatestSnapshotId(null);
              navigate({ to: '/' });
            }
          }

          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Portfolio deleted successfully',
          });
        } catch (error) {
          console.error('Error deleting portfolio:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete portfolio',
          });
        }
      },
    });
  };

  return (
    <div className="sidebar-fixed">
      <Toast ref={toast} />
      <div
        ref={sidebarRef}
        className={`sidebar-static ${isExpanded ? 'expanded' : ''}`}
        style={{
          width: isExpanded ? `${sidebarWidth}px` : '56px',
        }}
      >
        <div
          className="sidebar-content"
          style={{
            width: isExpanded ? `calc(${sidebarWidth}px - 1px)` : '55px',
          }}
        >
          {/* Header with Logo */}
          <div
            className="sidebar-header"
            style={{
              padding: isExpanded ? '16px' : '8px',
              borderBottom: isExpanded
                ? '1px solid var(--bq-border-muted, var(--surface-border))'
                : 'none',
              justifyContent: isExpanded ? 'space-between' : 'center',
            }}
          >
            {isExpanded ? (
              <>
                <div>
                  <div className="flex align-items-center">
                    <img
                      src="/logo.svg"
                      alt="Banqora"
                      style={{
                        width: '21px',
                        height: '21px',
                        marginRight: '8px',
                      }}
                    />
                    <span
                      className="font-bold"
                      style={{
                        fontSize: '18px',
                        color: 'var(--bq-text-primary, var(--text-color))',
                      }}
                    >
                      Visible Alpha
                    </span>
                  </div>
                  <p
                    className="m-0 mt-1"
                    style={{
                      fontSize: '12px',
                      color:
                        'var(--bq-text-secondary, var(--text-color-secondary))',
                      paddingLeft: '32px',
                    }}
                  >
                    Powered by Banqora
                  </p>
                </div>
                <Button
                  icon="pi pi-angle-double-left"
                  className="color-text-inverted"
                  style={{
                    background: 'none',
                    border: 'none',
                    boxShadow: 'none',
                    color: 'var(--bq-text-primary, var(--text-color))',
                  }}
                  onClick={() => onExpandToggle(false)}
                  text
                  aria-label="collapse sidebar"
                />
              </>
            ) : (
              <img
                src="/logo.svg"
                alt="Banqora"
                style={{ width: '24px', height: '24px' }}
              />
            )}
          </div>

          {/* Expand button (collapsed state only) */}
          {!isExpanded && (
            <div className="expand-btn-container">
              <Button
                icon="pi pi-angle-double-right"
                className="color-text-inverted"
                style={{
                  background: 'none',
                  border: 'none',
                  boxShadow: 'none',
                  width: '40px',
                  height: '40px',
                  color: 'var(--bq-text-primary, var(--text-color))',
                }}
                onClick={() => onExpandToggle(true)}
                text
                aria-label="expand sidebar"
              />
            </div>
          )}

          {/* Portfolios Section */}
          {hasAccess('portfolio') && (
            <div
              className="portfolio-section"
              style={{
                paddingBottom: 0,
                borderBottom: isExpanded
                  ? '1px solid var(--surface-border)'
                  : 'none',
                flex: isExpanded ? '1 1 auto' : '0 0 auto',
                minHeight: isExpanded ? '100px' : 'auto',
              }}
            >
              {isExpanded ? (
                <>
                  {/* Section Header */}
                  <div
                    className="section-header"
                    style={{
                      padding: '12px 16px 8px 16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color:
                        'var(--bq-text-secondary, var(--text-color-secondary))',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Portfolios
                  </div>

                  {/* Portfolio list skeleton when loading */}
                  {portfoliosLoading && (
                    <div
                      className="portfolio-list"
                      style={{ padding: '0 16px' }}
                      data-testid="portfolio-list-skeleton"
                    >
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div
                          key={i}
                          style={{
                            padding: '10px 0',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Skeleton
                            width="100%"
                            height="1rem"
                            style={{ borderRadius: '4px' }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Search Input - only show when portfolios exist and not loading */}
                  {!portfoliosLoading && portfolios.length > 0 && (
                    <div style={{ padding: '0 16px 8px 16px' }}>
                      <InputText
                        value={portfolioSearch}
                        onChange={(e) => setPortfolioSearch(e.target.value)}
                        placeholder="Search portfolios..."
                        className="w-full"
                        style={{
                          fontSize: '14px',
                          padding: '8px 12px',
                        }}
                      />
                    </div>
                  )}

                  {/* Create New Portfolio Button - hide when loading */}
                  {!portfoliosLoading && canCreate('portfolio') && (
                    <div
                      style={{
                        padding: '8px 16px',
                        borderBottom:
                          portfolios.length > 0
                            ? '1px solid var(--bq-border-muted)'
                            : 'none',
                      }}
                    >
                      <Button
                        label="Create New Portfolio"
                        icon="pi pi-plus"
                        className="w-full"
                        size="small"
                        outlined
                        onClick={() => setCreatePortfolioVisible(true)}
                        style={{
                          justifyContent: 'flex-start',
                          padding: '0.5rem 0.75rem',
                        }}
                      />
                    </div>
                  )}

                  {/* Portfolio List - only show when loaded (and portfolios exist or empty state) */}
                  {!portfoliosLoading && portfolios.length > 0 && (
                    <div className="portfolio-list">
                      {filteredPortfolios.map((portfolio) => (
                        <div
                          key={portfolio.id}
                          className={`portfolio-list-item portfolio-item ${
                            isPortfolioActive(portfolio.id) ? 'active' : ''
                          }`}
                          onClick={() => handlePortfolioChange(portfolio.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handlePortfolioChange(portfolio.id);
                            }
                          }}
                        >
                          <div
                            className="name"
                            style={{
                              fontWeight: isPortfolioActive(portfolio.id)
                                ? 600
                                : 400,
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {portfolio.name}
                          </div>
                          {filteredPortfolios.length > 1 &&
                            canDelete('portfolio') && (
                              <div
                                className="portfolio-actions"
                                style={{
                                  display: 'flex',
                                  gap: '0.5rem',
                                  alignItems: 'center',
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span
                                  className="pi pi-trash"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeletePortfolio(portfolio);
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    color:
                                      'var(--bq-text-secondary, var(--text-color-secondary))',
                                    fontSize: '14px',
                                    padding: '4px',
                                  }}
                                />
                              </div>
                            )}
                        </div>
                      ))}
                      {filteredPortfolios.length === 0 && (
                        <div
                          style={{
                            padding: '16px',
                            textAlign: 'center',
                            color:
                              'var(--bq-text-secondary, var(--text-color-secondary))',
                            fontSize: '14px',
                          }}
                        >
                          No portfolios found
                        </div>
                      )}
                    </div>
                  )}

                  {/* Empty state - only when loaded and no portfolios */}
                  {!portfoliosLoading &&
                    portfolios.length === 0 &&
                    !canCreate('portfolio') && (
                      <div
                        style={{
                          padding: '16px',
                          textAlign: 'center',
                          color:
                            'var(--bq-text-secondary, var(--text-color-secondary))',
                          fontSize: '14px',
                        }}
                      >
                        No portfolios
                      </div>
                    )}
                </>
              ) : (
                <>
                  <Tooltip
                    target=".portfolio-icon-collapsed"
                    content="Portfolios"
                    position="right"
                    pt={{
                      root: {
                        style: {
                          zIndex: 9999,
                        },
                      },
                    }}
                  />
                  <div
                    className="portfolio-icon-collapsed"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '56px',
                      height: '56px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                    }}
                    onClick={() => onExpandToggle(true)}
                  >
                    <i
                      className="pi pi-chart-line"
                      style={{ fontSize: '20px' }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Main Content */}
          <div
            style={{ flex: '0 0 auto', paddingTop: '8px', overflowY: 'auto' }}
          >
            {/* Views Section */}
            {!isPopout && hasAccess('dashboard') && (
              <div className="sidebar-section" style={{ marginBottom: '16px' }}>
                {isExpanded && <div className="section-header">Views</div>}

                {/* Overview */}
                <Tooltip
                  target=".nav-overview"
                  content="Overview"
                  position="right"
                  disabled={isExpanded}
                  pt={{
                    root: {
                      style: {
                        zIndex: 9999,
                      },
                    },
                  }}
                />
                <div
                  className={`nav-overview nav-item ${
                    isViewActive('overview') ? 'active' : 'inactive'
                  }`}
                  style={{
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                  }}
                  onClick={() => {
                    if (portfolioIdFromAtom) {
                      navigate({
                        to: '/portfolio/$portfolioId/overview',
                        params: { portfolioId: portfolioIdFromAtom },
                      });
                    }
                  }}
                >
                  <i
                    className="pi pi-home"
                    style={{
                      fontSize: '20px',
                      marginRight: isExpanded ? '8px' : 0,
                    }}
                  />
                  {isExpanded && <span>Overview</span>}
                </div>

                {/* Risk and Opportunities */}
                <Tooltip
                  target=".nav-risk-opportunities"
                  content="Risk and Opportunities"
                  position="right"
                  disabled={isExpanded}
                  pt={{
                    root: {
                      style: {
                        zIndex: 9999,
                      },
                    },
                  }}
                />
                <div
                  className={`nav-risk-opportunities nav-item ${
                    isViewActive('risk-opportunities') ? 'active' : 'inactive'
                  }`}
                  style={{
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                  }}
                  onClick={() => {
                    if (portfolioIdFromAtom) {
                      navigate({
                        to: '/portfolio/$portfolioId/risks',
                        params: { portfolioId: portfolioIdFromAtom },
                      });
                    }
                  }}
                >
                  <i
                    className="pi pi-lightbulb"
                    style={{
                      fontSize: '20px',
                      marginRight: isExpanded ? '8px' : 0,
                    }}
                  />
                  {isExpanded && <span>Risk and Opportunities</span>}
                </div>

                {/* Analysis */}
                <Tooltip
                  target=".nav-analysis"
                  content="Analysis"
                  position="right"
                  disabled={isExpanded}
                  pt={{
                    root: {
                      style: {
                        zIndex: 9999,
                      },
                    },
                  }}
                />
                <div
                  className={`nav-analysis nav-item ${
                    isViewActive('analysis') ? 'active' : 'inactive'
                  }`}
                  style={{
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                  }}
                  onClick={() => {
                    if (portfolioIdFromAtom) {
                      navigate({
                        to: '/portfolio/$portfolioId/analysis',
                        params: { portfolioId: portfolioIdFromAtom },
                      });
                    }
                  }}
                >
                  <i
                    className="pi pi-chart-line"
                    style={{
                      fontSize: '20px',
                      marginRight: isExpanded ? '8px' : 0,
                    }}
                  />
                  {isExpanded && <span>Analysis</span>}
                </div>

                {/* AI Recommendations */}
                <Tooltip
                  target=".nav-ai-recommendations"
                  content="AI Recommendations"
                  position="right"
                  disabled={isExpanded}
                  pt={{
                    root: {
                      style: {
                        zIndex: 9999,
                      },
                    },
                  }}
                />
                <div
                  className={`nav-ai-recommendations nav-item ${
                    isViewActive('ai-recommendations') ? 'active' : 'inactive'
                  }`}
                  style={{
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                  }}
                  onClick={() => {
                    if (portfolioIdFromAtom) {
                      navigate({
                        to: '/portfolio/$portfolioId/ai-recommendations',
                        params: { portfolioId: portfolioIdFromAtom },
                      });
                    }
                  }}
                >
                  <i
                    className="pi pi-sparkles"
                    style={{
                      fontSize: '20px',
                      marginRight: isExpanded ? '8px' : 0,
                    }}
                  />
                  {isExpanded && <span>AI Recommendations</span>}
                </div>
              </div>
            )}

            {/* Agent Chat */}
            <div className="sidebar-section" style={{ marginBottom: '16px' }}>
              <Tooltip
                target=".nav-agent-chat"
                content="Agent Chat"
                position="right"
                disabled={isExpanded}
                pt={{
                  root: {
                    style: {
                      zIndex: 9999,
                    },
                  },
                }}
              />
              <Button
                className={`nav-agent-chat ${isExpanded ? 'expanded' : ''} p-button-primary`}
                icon="pi pi-comments"
                label={isExpanded ? 'Agent Chat' : undefined}
                onClick={() => setShowChat(!showChat)}
                aria-label="Agent Chat"
                style={{
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isExpanded ? 'flex-start' : 'center',
                  margin: isExpanded ? '0 18px' : '0 auto',
                  padding: isExpanded ? '0 50px' : '',
                  width: isExpanded ? 'auto' : '40px',
                }}
              />
            </div>

            {/* Divider before Data Section */}
            {!isPopout && (
              <div
                className="sidebar-divider"
                style={{
                  height: '1px',
                  backgroundColor:
                    'var(--bq-border-muted, var(--surface-border))',
                  margin: '8px 0',
                  width: '100%',
                }}
              />
            )}

            {/* Data Section */}
            {!isPopout && (
              <div className="sidebar-section">
                {isExpanded && <div className="section-header">Data</div>}

                {/* Upload */}
                {hasAccess('document') && portfolioIdFromAtom && (
                  <>
                    <Tooltip
                      target=".nav-upload"
                      content="Upload"
                      position="right"
                      disabled={isExpanded}
                      pt={{
                        root: {
                          style: {
                            zIndex: 9999,
                          },
                        },
                      }}
                    />
                    <div
                      className={`nav-upload nav-item ${
                        isViewActive('upload') ? 'active' : 'inactive'
                      }`}
                      style={{
                        justifyContent: isExpanded ? 'flex-start' : 'center',
                      }}
                      onClick={() => {
                        if (portfolioIdFromAtom) {
                          navigate({
                            to: '/portfolio/$portfolioId/upload',
                            params: { portfolioId: portfolioIdFromAtom },
                          });
                        }
                      }}
                    >
                      <i
                        className="pi pi-upload"
                        style={{
                          fontSize: '20px',
                          marginRight: isExpanded ? '8px' : 0,
                        }}
                      />
                      {isExpanded && <span>Upload</span>}
                    </div>
                  </>
                )}

                {/* Portfolio Settings */}
                {hasAccess('portfolio') && (
                  <>
                    <Tooltip
                      target=".nav-portfolio-settings"
                      content="Portfolio Settings"
                      position="right"
                      disabled={isExpanded}
                      pt={{
                        root: {
                          style: {
                            zIndex: 9999,
                          },
                        },
                      }}
                    />
                    <div
                      className={`nav-portfolio-settings ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => setEditPortfolioVisible(true)}
                      style={{
                        height: '40px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isExpanded ? 'flex-start' : 'center',
                      }}
                    >
                      <i
                        className="pi pi-cog"
                        style={{
                          fontSize: '20px',
                          marginRight: isExpanded ? '8px' : 0,
                        }}
                      />
                      {isExpanded && <span>Portfolio Settings</span>}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Settings */}
            {!isPopout && (
              <div className="sidebar-section" style={{ marginBottom: '16px' }}>
                <Tooltip
                  target=".nav-settings"
                  content="Settings"
                  position="right"
                  disabled={isExpanded}
                  pt={{
                    root: {
                      style: {
                        zIndex: 9999,
                      },
                    },
                  }}
                />
                <Link
                  to="/settings-member"
                  className={`nav-settings ${isExpanded ? 'expanded' : ''} ${location.pathname === '/settings-member' ? 'active' : ''}`}
                  style={{
                    height: '40px',
                    paddingLeft: '16px',
                    paddingRight: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isExpanded ? 'flex-start' : 'center',
                    textDecoration: 'none',
                    color: 'var(--bq-text-primary)',
                    backgroundColor:
                      location.pathname === '/settings-member'
                        ? 'var(--bq-sidebar-view-active-bg, var(--bq-surface-muted))'
                        : 'transparent',
                    borderLeft:
                      location.pathname === '/settings-member'
                        ? '3px solid var(--bq-color-primary)'
                        : '3px solid transparent',
                    fontWeight:
                      location.pathname === '/settings-member' ? 600 : 400,
                  }}
                >
                  <i
                    className="pi pi-sliders-h"
                    style={{
                      fontSize: '20px',
                      marginRight: isExpanded ? '8px' : 0,
                    }}
                  />
                  {isExpanded && <span>Settings</span>}
                </Link>
              </div>
            )}
          </div>

          {/* Version display at bottom */}
          {isExpanded && appVersion && (
            <div
              className="version-display"
              style={{
                marginTop: 'auto',
                padding: '16px',
                borderTop: '1px solid var(--surface-border)',
                fontSize: '12px',
                color: 'var(--bq-text-secondary, var(--text-color-secondary))',
                textAlign: 'center',
              }}
            >
              v{appVersion}
            </div>
          )}
        </div>

        {/* Resize handle */}
        {isExpanded && (
          <div
            onMouseDown={handleMouseDown}
            style={{
              position: 'absolute',
              top: 0,
              right: '-4px',
              width: '8px',
              height: '100%',
              cursor: 'ew-resize',
              backgroundColor: 'transparent',
              zIndex: 2,
            }}
          />
        )}
      </div>

      {/* Chat Panel - shows as overlay when opened */}
      {showChat && (
        <div
          style={{
            position: 'fixed',
            left: isExpanded ? `${sidebarWidth}px` : '56px',
            top: 0,
            width: `${chatWidth}px`,
            height: '100vh',
            backgroundColor: 'var(--bq-surface-card, var(--surface-card))',
            borderRight:
              '1px solid var(--bq-border-muted, var(--surface-border))',
            zIndex: 1000,
            boxShadow: '2px 0 8px var(--bq-shadow-color, rgba(0,0,0,0.1))',
          }}
        >
          <StreamChat
            initialMessage="If you have any specific questions or need further analysis, feel free to ask!"
            onExpandToggle={() => setShowChat(false)}
            dashboardId={dashboardId ?? undefined}
          />

          {/* Right-side resize handle for chat panel */}
          <div
            onMouseDown={handleChatMouseDown}
            style={{
              position: 'absolute',
              top: 0,
              right: '-2px',
              width: '8px',
              height: '100%',
              cursor: 'ew-resize',
              backgroundColor: isChatResizing
                ? 'var(--bq-surface-muted, var(--surface-hover))'
                : 'transparent',
              zIndex: 10,
              borderLeft: isChatResizing
                ? '2px solid var(--bq-color-primary)'
                : '2px solid var(--bq-border-muted, var(--surface-border))',
              boxShadow: isChatResizing
                ? '0 0 8px var(--bq-color-primary)'
                : 'none',
            }}
            title="Drag to resize chat panel width"
          />
        </div>
      )}

      {/* Portfolio Settings Dialog */}
      <EditPortfolioDialog
        visible={editPortfolioVisible}
        onHide={() => setEditPortfolioVisible(false)}
      />

      <CreatePortfolioStepperDialog
        visible={createPortfolioVisible}
        onHide={() => setCreatePortfolioVisible(false)}
      />

      <ConfirmDialog />
    </div>
  );
};
